import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { AlertTriangle, AlertCircle, Clock, User, BookOpen, Building2, ChevronDown, ChevronRight } from "lucide-react";

const SEVERITY = {
  expired: { l: "Vencido", cls: "bg-rose-100 text-rose-700", Icon: AlertCircle },
  expiring_soon: { l: "Vence em breve", cls: "bg-amber-100 text-amber-700", Icon: AlertTriangle },
  pending: { l: "Pendente", cls: "bg-slate-100 text-slate-600", Icon: Clock },
  in_progress: { l: "Em andamento", cls: "bg-sky-100 text-sky-700", Icon: Clock },
};

export default function ComplianceAlerts() {
  const [d, setD] = useState(null);
  const [openGroup, setOpenGroup] = useState({});
  useEffect(() => { api.get("/alerts/compliance").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-slate-500">Carregando…</div>;

  const toggle = (k) => setOpenGroup((s) => ({ ...s, [k]: !s[k] }));

  const Summary = ({ s }) => (
    <div className="flex gap-1.5">
      {s.expired > 0 && <Badge className="bg-rose-100 text-rose-700 border-0 text-[10px]">{s.expired} vencido{s.expired > 1 ? "s" : ""}</Badge>}
      {s.expiring_soon > 0 && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">{s.expiring_soon} vencendo</Badge>}
      {s.pending > 0 && <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">{s.pending} pendente{s.pending > 1 ? "s" : ""}</Badge>}
      {s.in_progress > 0 && <Badge className="bg-sky-100 text-sky-700 border-0 text-[10px]">{s.in_progress} em andamento</Badge>}
    </div>
  );

  const renderGroup = (kind, groups, IconMain, titleKey, subtitleKey, linkFor) => (
    <div className="space-y-2">
      {groups.length === 0 && <div className="text-center text-slate-500 py-8">Sem pendências neste agrupamento.</div>}
      {groups.map((g, i) => {
        const key = `${kind}-${i}`;
        const open = !!openGroup[key];
        return (
          <Card key={key} className="border-[var(--unigr-border)]" data-testid={`${kind}-group-${i}`}>
            <button className="w-full text-left" onClick={() => toggle(key)}>
              <CardContent className="p-4 flex items-center gap-3">
                <IconMain className="w-5 h-5 text-[var(--unigr-navy)] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold truncate">{g[titleKey]}</div>
                  {subtitleKey && g[subtitleKey] && <div className="text-xs text-slate-500 truncate">{g[subtitleKey]}</div>}
                </div>
                <div className="font-mono text-sm text-slate-600">{g.total}</div>
                <Summary s={g.summary} />
                {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
              </CardContent>
            </button>
            {open && (
              <div className="border-t border-[var(--unigr-border)] px-4 py-3 bg-slate-50">
                <table className="w-full text-xs">
                  <thead className="text-slate-500 uppercase text-[10px]">
                    <tr>
                      {kind !== "operator" && <th className="text-left py-1">Colaborador</th>}
                      {kind !== "course" && <th className="text-left py-1">Curso</th>}
                      {kind !== "department" && <th className="text-left py-1">Departamento</th>}
                      <th className="text-left py-1">Situação</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.slice(0, 50).map((it, idx) => {
                      const S = SEVERITY[it.severity] || SEVERITY.pending;
                      return (
                        <tr key={idx} className="border-t border-white">
                          {kind !== "operator" && <td className="py-1.5">{it.user_name}</td>}
                          {kind !== "course" && <td className="py-1.5">{it.course_title}</td>}
                          {kind !== "department" && <td className="py-1.5 text-slate-500">{it.department || "—"}</td>}
                          <td className="py-1.5">
                            <span className={"px-1.5 py-0.5 rounded text-[10px] inline-flex items-center gap-1 " + S.cls}>
                              <S.Icon className="w-3 h-3" />{S.l}{it.days_left != null && it.severity === "expiring_soon" ? ` (${it.days_left}d)` : ""}
                            </span>
                          </td>
                          <td className="py-1.5 text-right">
                            {linkFor && (
                              <Link to={linkFor(it)} className="text-[var(--unigr-navy)] hover:underline">Ver</Link>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {g.items.length > 50 && <tr><td colSpan="5" className="text-center py-2 text-slate-400 text-[10px]">+{g.items.length - 50} outros</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="compliance-alerts-page">
      <header>
        <div className="eyebrow">Compliance operacional</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Alertas de conformidade</h1>
        <p className="text-slate-600 text-sm mt-2">Todos os cursos vencidos ou pendentes, agrupados. Total: <strong>{d.total}</strong> pendência(s).</p>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPI k="Vencidos" v={d.totals.expired} cls="bg-rose-50 text-rose-700" />
        <KPI k="Vencendo (≤30d)" v={d.totals.expiring_soon} cls="bg-amber-50 text-amber-700" />
        <KPI k="Pendentes" v={d.totals.pending} cls="bg-slate-50 text-slate-700" />
        <KPI k="Em andamento" v={d.totals.in_progress} cls="bg-sky-50 text-sky-700" />
      </div>

      <Tabs defaultValue="operator">
        <TabsList>
          <TabsTrigger value="operator" data-testid="tab-by-operator"><User className="w-4 h-4 mr-1" />Por colaborador ({d.by_operator.length})</TabsTrigger>
          <TabsTrigger value="course" data-testid="tab-by-course"><BookOpen className="w-4 h-4 mr-1" />Por curso ({d.by_course.length})</TabsTrigger>
          <TabsTrigger value="department" data-testid="tab-by-department"><Building2 className="w-4 h-4 mr-1" />Por departamento ({d.by_department.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="operator" className="mt-4">
          {renderGroup("operator", d.by_operator, User, "user_name", "position", (it) => `/collaborators/${it.user_id}`)}
        </TabsContent>
        <TabsContent value="course" className="mt-4">
          {renderGroup("course", d.by_course, BookOpen, "course_title", null, (it) => `/collaborators/${it.user_id}`)}
        </TabsContent>
        <TabsContent value="department" className="mt-4">
          {renderGroup("department", d.by_department, Building2, "department", null, (it) => `/collaborators/${it.user_id}`)}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KPI({ k, v, cls }) {
  return (
    <Card><CardContent className="p-4">
      <div className="text-xs text-slate-500 uppercase">{k}</div>
      <div className={"inline-block mt-2 px-3 py-1 rounded font-bold " + cls}>{v}</div>
    </CardContent></Card>
  );
}
